import Document from "../../entities/document";

/**
 * Document actions
 *
 * Call order:
 *
 * DOCUMENT_SAVE_QUEUE:	adds the item to the save queue
 * --> DOCUMENT_SAVE:	modifies the state
 * --> DOCUMENT_SAVE_SUCCESS: if all went well
 * --> DOCUMENT_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail DOCUMENT_SAVE_QUEUE is during the call to DOCUMENT_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at DOCUMENT_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: DOCUMENT_SAVE_SUCCESS, DOCUMENT_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or DOCUMENT_PENDING_CHECK (it left the queue).
 */

export const DOCUMENT_FETCH_ONE_QUEUE: any = 'DOCUMENT_FETCH_ONE_QUEUE';
export const DOCUMENT_FETCH_SUCCESS: any = 'DOCUMENT_FETCH_SUCCESS';
export const DOCUMENT_FETCH_FAILURE: any = 'DOCUMENT_FETCH_FAILURE';
export const DOCUMENT_FETCH_ONE_SUCCESS: any = 'DOCUMENT_FETCH_ONE_SUCCESS';
export const DOCUMENT_FETCH_ONE_FAILURE: any = 'DOCUMENT_FETCH_ONE_FAILURE';

export const DOCUMENT_SAVE: any = 'DOCUMENT_SAVE';
export const DOCUMENT_SAVE_QUEUE: any = 'DOCUMENT_SAVE_QUEUE';
export const DOCUMENT_SAVE_SUCCESS: any = 'DOCUMENT_SAVE_SUCCESS';
export const DOCUMENT_SAVE_FAILURE: any = 'DOCUMENT_SAVE_FAILURE';

export const DOCUMENT_SAVE_MANY: any = 'DOCUMENT_SAVE_MANY';
export const DOCUMENT_SAVE_MANY_QUEUE: any = 'DOCUMENT_SAVE_MANY_QUEUE';
export const DOCUMENT_SAVE_MANY_SUCCESS: any = 'DOCUMENT_SAVE_MANY_SUCCESS';
export const DOCUMENT_SAVE_MANY_FAILURE: any = 'DOCUMENT_SAVE_MANY_FAILURE';

export const DOCUMENT_REMOVE: any = 'DOCUMENT_REMOVE';
export const DOCUMENT_REMOVE_QUEUE: any = 'DOCUMENT_REMOVE_QUEUE';
export const DOCUMENT_REMOVE_SUCCESS: any = 'DOCUMENT_REMOVE_SUCCESS';
export const DOCUMENT_REMOVE_FAILURE: any = 'DOCUMENT_REMOVE_FAILURE';

export const DOCUMENT_RESTORE: any = 'DOCUMENT_RESTORE';
export const DOCUMENT_RESTORE_QUEUE: any = 'DOCUMENT_RESTORE_QUEUE';
export const DOCUMENT_RESTORE_SUCCESS: any = 'DOCUMENT_RESTORE_SUCCESS';
export const DOCUMENT_RESTORE_FAILURE: any = 'DOCUMENT_RESTORE_FAILURE';

export const DOCUMENT_RESTORE_MANY: any = 'DOCUMENT_RESTORE_MANY';
export const DOCUMENT_REMOVE_MANY: any = 'DOCUMENT_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const DOCUMENT_SAVE_REVERT: any = 'DOCUMENT_SAVE_REVERT';
export const DOCUMENT_PENDING_CHECK: any = 'DOCUMENT_PENDING_CHECK';

/* Fetch actions */
export const fetchDocumentSuccess = (documents: Document[], partial: boolean, lastFetch: number) => ({type: DOCUMENT_FETCH_SUCCESS, documents, partial, lastFetch});
export const fetchDocumentFailure = (err: any) => {
		console.error('DocumentAction: fetchDocumentFailure', err);
		return {type: DOCUMENT_FETCH_FAILURE, err};
};
export const fetchOneDocumentQueue = (documentId: string, sentAddedOn?: number) => ({type: DOCUMENT_FETCH_ONE_QUEUE, documentId, sentAddedOn});
export const fetchOneDocumentSuccess = (document: Document | null, sentAddedOn?: number, documentId: string = document?.id) => ({type: DOCUMENT_FETCH_ONE_SUCCESS, documentId, document, sentAddedOn});
export const fetchOneDocumentFailure = (id: string, err: any) => {
		console.error('DocumentAction: fetchOneDocumentFailure', err);
		return {type: DOCUMENT_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveDocument = (document: Document) => {
    // console.debug('⮂ DOCUMENT_SAVE action', document?.id);
    return {type: DOCUMENT_SAVE, document};
}
export const saveDocumentQueue = (document: Document, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ DOCUMENT_SAVE_QUEUE action', document?.id);
    return {type: DOCUMENT_SAVE_QUEUE, document, onQueued};
}
export const saveDocumentSuccess = (document: Document, sentAddedOn?: number) => {
    // console.debug('⮂ DOCUMENT_SAVE_SUCCESS action', document?.id);
    return {type: DOCUMENT_SAVE_SUCCESS, document, sentAddedOn};
}
export const saveDocumentFailure = (document: Document, status: number, message: string) => {
    console.warn(`⮂ DOCUMENT_SAVE_FAILURE error saving document: HTTP ${status}: ${message}`, document?.id);
    return {type: DOCUMENT_SAVE_FAILURE, document, status, message};
};

/* SaveMany actions */
export const saveManyDocuments = (documents: Document[]) => {
    // console.debug('⮂ DOCUMENT_SAVE_MANY action', documents?.map(document => document?.id).join(','));
    return {type: DOCUMENT_SAVE_MANY, documents};
}
export const saveManyDocumentsQueue = (documents: Document[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ DOCUMENT_SAVE_MANY_QUEUE action', documents?.map(document => document.id).join(','));
    return {type: DOCUMENT_SAVE_MANY_QUEUE, documents, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `documents` (OWA-1018) */
export const saveManyDocumentsSuccess = (documents: Document[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ DOCUMENT_SAVE_MANY_SUCCESS action', documents?.map(document => document.id).join(','));
    return {type: DOCUMENT_SAVE_MANY_SUCCESS, documents, sentAddedOns};
}
export const saveManyDocumentsFailure = (documents: Document[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_DOCUMENTS_FAILURE Error saving documents: HTTP ${status}: ${message}`, documents?.map(document => document.id).join(','));
		return {type: DOCUMENT_SAVE_MANY_FAILURE, documents, status, message};
};

/* Remove actions */
export const removeDocument = (document: Document) => ({ type: DOCUMENT_REMOVE, document });
export const removeDocumentQueue = (document: Document) => ({ type: DOCUMENT_REMOVE_QUEUE, document });
export const removeDocumentSuccess = (document: Document) => ({ type: DOCUMENT_REMOVE_SUCCESS, document });
export const removeDocumentFailure = (document: Document, status: number, message: string) => {
	console.error(`Redux: removeDocumentFailure Error removing document: HTTP ${status}: ${message}`);
	return ({ type: DOCUMENT_REMOVE_FAILURE, document, status, message });
};

/* Restore actions */
export const restoreDocument = (document: Document) => ({ type: DOCUMENT_RESTORE, document });
export const restoreDocumentQueue = (document: Document) => ({ type: DOCUMENT_RESTORE_QUEUE, document });
export const restoreDocumentSuccess = (document: Document, sentAddedOn?: number) => ({ type: DOCUMENT_RESTORE_SUCCESS, document, sentAddedOn });
export const restoreDocumentFailure = (document: Document, status: number, message: string) => {
	console.error(`Redux: restoreDocumentFailure Error restoring document: HTTP ${status}: ${message}`);
	return ({ type: DOCUMENT_RESTORE_FAILURE, document, status, message });
};

/* RestoreMany actions */
export const restoreManyDocuments = (documents: Document[]) => ({ type: DOCUMENT_RESTORE_MANY, documents });

/* RemoveMany actions */
export const removeManyDocuments = (documents: Document[]) => ({ type: DOCUMENT_REMOVE_MANY, documents });

/* Pending save actions (OWA-1018) */
/**
 * The save `document` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertDocumentSave = (document: Document, options: {sentAddedOn?: number, current?: Document | null, keep?: boolean} = {}) =>
    ({type: DOCUMENT_SAVE_REVERT, document, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkDocumentPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: DOCUMENT_PENDING_CHECK, live, refused, readAt});